import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  BOSSES,
  BOSS_KINDS,
  bossPreset,
  weaknessElements,
  type Boss,
  type BossKind,
} from "@/lib/bosses-data";
import { MAP_HOTSPOTS } from "@/lib/maps-data";
import { breadcrumbSchema, toolSchema } from "@/lib/schema";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { ShareButton } from "@/components/ShareButton";

const REGION_BY_BOSS: Record<string, string> = Object.fromEntries(
  MAP_HOTSPOTS.map((h) => [h.bossSlug, h.regionName]),
);

const FAQ: FaqItem[] = [
  {
    q: "What is the best boss order in Palworld?",
    a: `The fastest clear order is by boss level, not by map position: Zoe & Grizzbolt (Lv 10), Lily & Lyleen (Lv 20), Marcus & Faleris (Lv 30), Axel & Orserk (Lv 40), Victor & Shadowbeak (Lv 50), then the Sakurajima and Feybreak fights. The Pal Stars boss route planner sorts all ${BOSSES.length} Palworld bosses into that order for you and adds the alpha and raid detours that sit inside each level band.`,
  },
  {
    q: "What level should I be for each Palworld boss?",
    a: "Match or exceed the boss level and stay inside a five-level window. Fighting more than five levels under a boss applies a damage penalty that makes tower time limits unwinnable, so clear the alphas in the same region first to close the gap.",
  },
  {
    q: "How does the Palworld boss route planner work?",
    a: "Set your current level and pick which boss types you want to fight. The planner returns an ordered run: every boss inside your level window, grouped by Palpagos region so you travel each island once, with the recommended weapon, armour, counter element and party for each stop, plus a link to that boss's full guide.",
  },
  {
    q: "Does the planner remember which bosses I have cleared?",
    a: "Yes. Ticking a boss stores it in your browser, so your route rebuilds around the fights you still have left every time you return to the page. Clearing your browser storage resets the run.",
  },
  {
    q: "Can I plan raid bosses and predator bosses in the same route?",
    a: "Yes. Toggle Tower Boss, Raid Boss and Predator Boss on or off. Raid bosses are summoned at an altar rather than found in the world, so the planner lists them as end-of-band stops once you can survive their level band.",
  },
];

export const Route = createFileRoute("/boss-route")({
  head: () => ({
    meta: [
      { title: "Palworld Boss Route Planner — Best Boss Order by Level" },
      {
        name: "description",
        content: `Plan the fastest Palworld boss order. Pick your level and boss types to get all ${BOSSES.length} bosses routed by level and region with loadouts, weaknesses and guides.`,
      },
      { property: "og:title", content: "Palworld Boss Route Planner — Best Boss Order" },
      { property: "og:description", content: "An ordered Palworld boss run by level and region, with weapons, counter elements and counter Pals for every stop." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/boss-route" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld Boss Route Planner — Pal Stars" },
      { name: "twitter:description", content: "Route every Palworld tower, raid and predator boss by level and region." },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/boss-route" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Bosses", "/bosses"], ["Boss Route Planner", "/boss-route"]]),
      toolSchema({
        name: "Palworld Boss Route Planner",
        description: "Builds an ordered Palworld boss clearing route by level band and Palpagos region, with recommended weapons, armour, counter elements and counter Pals for every boss.",
        path: "/boss-route",
      }),
      faqSchema(FAQ, "https://palstars.com/boss-route"),
    ],
  }),
  component: BossRoutePage,
});

const DONE_KEY = "palstars:boss-route-done";

function BossRoutePage() {
  const [level, setLevel] = useState(10);
  const [kinds, setKinds] = useState<BossKind[]>([...BOSS_KINDS]);
  const [hideDone, setHideDone] = useState(true);
  const [done, setDone] = useState<string[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DONE_KEY);
      if (raw) setDone(JSON.parse(raw) as string[]);
    } catch {
      /* storage unavailable */
    }
  }, []);

  const toggleDone = (slug: string) => {
    setDone((prev) => {
      const next = prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug];
      try {
        localStorage.setItem(DONE_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable */
      }
      return next;
    });
  };

  const stops = useMemo(() => {
    return BOSSES.filter((b) => kinds.includes(b.kind))
      .filter((b) => !(hideDone && done.includes(b.slug)))
      .slice()
      .sort((a, b) => a.level - b.level || a.name.localeCompare(b.name));
  }, [kinds, hideDone, done]);

  /** Consecutive stops in the same region become one travel leg. */
  const legs = useMemo(() => {
    const out: { region: string; bosses: Boss[] }[] = [];
    for (const b of stops) {
      const region = REGION_BY_BOSS[b.slug] ?? "Summoned / instanced arena";
      const last = out[out.length - 1];
      if (last && last.region === region) last.bosses.push(b);
      else out.push({ region, bosses: [b] });
    }
    return out;
  }, [stops]);

  const next = stops.find((b) => b.level >= level) ?? stops[0];
  const behind = stops.filter((b) => b.level < level - 5).length;
  const gearChecks = useMemo(() => {
    const seen = new Set<string>();
    const out: { atLevel: number; weapon: string; armour: string }[] = [];
    for (const b of stops) {
      const p = bossPreset(b);
      const key = `${p.weapon}|${p.armour}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ atLevel: b.level, weapon: p.weapon, armour: p.armour });
    }
    return out;
  }, [stops]);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/" className="hover:text-foreground">Home</Link> /{" "}
        <Link to="/bosses" className="hover:text-foreground">Bosses</Link> /{" "}
        <span className="text-foreground">Boss Route Planner</span>
      </nav>
      <h1 className="text-3xl sm:text-4xl font-bold mt-3">Palworld Boss Route Planner</h1>
      <p className="aeo-answer mt-2 max-w-3xl text-muted-foreground">
        The fastest Palworld boss order is by level, not by map position. Set your current level and the
        planner routes all {BOSSES.length} tower, raid and predator bosses into a single run — sorted by
        level band, grouped so you only travel each Palpagos region once, with the recommended weapon,
        armour, counter element and counter Pals for every stop.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <ShareButton
          title="Palworld Boss Route Planner"
          text="Plan the fastest Palworld boss order by level and region."
          path="/boss-route"
        />
        <Link to="/map" className="text-sm text-primary hover:underline">Open the interactive map →</Link>
        <Link to="/damage" search={{ boss: undefined }} className="text-sm text-primary hover:underline">
          Damage calculator →
        </Link>
      </div>

      <section className="glass rounded-2xl p-5 mt-6">
        <h2 className="text-lg font-bold">Route settings</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="level" className="text-sm font-medium">
              Your level: <span className="text-primary">{level}</span>
            </label>
            <input
              id="level"
              type="range"
              min={1}
              max={60}
              value={level}
              onChange={(e) => setLevel(Number(e.target.value))}
              className="mt-2 w-full"
              aria-describedby="level-help"
            />
            <p id="level-help" className="mt-1 text-xs text-muted-foreground">
              Stops more than five levels below you are marked as overlevelled clean-up.
            </p>
          </div>
          <fieldset>
            <legend className="text-sm font-medium">Boss types in the route</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {BOSS_KINDS.map((k) => {
                const on = kinds.includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setKinds((prev) => (on ? prev.filter((x) => x !== k) : [...prev, k]))}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${on ? "bg-primary text-primary-foreground border-primary" : "hover:bg-card"}`}
                  >
                    {k}
                  </button>
                );
              })}
            </div>
            <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} />
              Hide bosses I have already cleared ({done.length} cleared)
            </label>
          </fieldset>
        </div>
      </section>

      <div className="mt-6 grid gap-4 sm:grid-cols-3" aria-live="polite">
        <div className="glass rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Next fight</div>
          <div className="mt-1 font-bold">{next ? next.name : "Route complete"}</div>
          {next && (
            <p className="mt-1 text-xs text-muted-foreground">
              Lv {next.level} · weak to {weaknessElements(next).join(" / ")}
            </p>
          )}
        </div>
        <div className="glass rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Stops remaining</div>
          <div className="mt-1 font-bold">{stops.length}</div>
          <p className="mt-1 text-xs text-muted-foreground">{legs.length} travel legs</p>
        </div>
        <div className="glass rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Overlevelled clean-up</div>
          <div className="mt-1 font-bold">{behind}</div>
          <p className="mt-1 text-xs text-muted-foreground">Bosses more than 5 levels below you</p>
        </div>
      </div>

      <section className="mt-10">
        <h2 className="text-2xl font-bold">Your route ({stops.length} stops)</h2>
        <ol className="mt-5 space-y-6">
          {legs.map((leg, li) => (
            <li key={`${leg.region}-${li}`} className="glass rounded-2xl p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-lg font-bold">
                  Leg {li + 1}: {leg.region}
                </h3>
                <span className="text-xs text-muted-foreground">
                  {leg.bosses.length} boss{leg.bosses.length === 1 ? "" : "es"} · Lv{" "}
                  {leg.bosses[0].level}–{leg.bosses[leg.bosses.length - 1].level}
                </span>
              </div>
              <ul className="mt-4 space-y-4">
                {leg.bosses.map((b) => {
                  const p = bossPreset(b);
                  const isDone = done.includes(b.slug);
                  const under = b.level > level;
                  return (
                    <li key={b.slug} className="rounded-xl border p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block h-2.5 w-2.5 rounded-full"
                              style={{ background: b.color }}
                              aria-hidden="true"
                            />
                            <h4 className="font-semibold">{b.name}</h4>
                            <span className="text-xs text-muted-foreground">Lv {b.level} · {b.kind}</span>
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground">{b.location}</p>
                        </div>
                        <label className="flex shrink-0 items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={isDone}
                            onChange={() => toggleDone(b.slug)}
                            aria-label={`Mark ${b.name} as cleared`}
                          />
                          Cleared
                        </label>
                      </div>
                      <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                        <div><dt className="text-muted-foreground">Weak to</dt><dd className="font-medium">{weaknessElements(b).join(", ")}</dd></div>
                        <div><dt className="text-muted-foreground">Resists</dt><dd className="font-medium">{b.resists}</dd></div>
                        <div><dt className="text-muted-foreground">Weapon</dt><dd className="font-medium">{p.weapon}</dd></div>
                        <div><dt className="text-muted-foreground">Armour</dt><dd className="font-medium">{p.armour}</dd></div>
                        <div><dt className="text-muted-foreground">Party</dt><dd className="font-medium">{p.party.join(", ")}</dd></div>
                        <div><dt className="text-muted-foreground">Spheres</dt><dd className="font-medium">{p.sphere}</dd></div>
                      </dl>
                      {under && (
                        <p className="mt-2 text-xs text-amber-400">
                          {b.level - level} level{b.level - level === 1 ? "" : "s"} above you — clear the
                          alphas in {leg.region} first.
                        </p>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Link
                          to="/guides/$slug"
                          params={{ slug: `palworld-${b.slug}-boss-guide` }}
                          className="rounded-full border px-3 py-1 text-xs hover:bg-card"
                        >
                          {b.name} guide →
                        </Link>
                        <Link
                          to="/damage"
                          search={{ boss: b.slug }}
                          className="rounded-full border border-primary/50 px-3 py-1 text-xs text-primary hover:bg-card"
                        >
                          Damage check →
                        </Link>
                        <Link to="/map" className="rounded-full border px-3 py-1 text-xs hover:bg-card">
                          Find on map →
                        </Link>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
          {stops.length === 0 && (
            <li className="glass rounded-2xl p-6 text-sm text-muted-foreground">
              Nothing left in this route. Turn a boss type back on, or untick some cleared bosses.
            </li>
          )}
        </ol>
      </section>

      <section className="glass rounded-2xl p-6 mt-10">
        <h2 className="text-xl font-bold">Gear checkpoints along the route</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Each upgrade below is the minimum loadout for the level band it appears in. Craft it before the
          stop it is listed against rather than mid-run.
        </p>
        <ul className="mt-4 space-y-2">
          {gearChecks.map((g) => (
            <li key={`${g.atLevel}-${g.weapon}-${g.armour}`} className="text-sm">
              <span className="font-semibold">By Lv {g.atLevel}:</span>{" "}
              <span className="text-muted-foreground">{g.weapon} · {g.armour}</span>
            </li>
          ))}
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld boss route FAQ" />

      <nav aria-label="Related Palworld boss tools" className="glass rounded-2xl p-6 mt-8">
        <h2 className="text-xl font-bold">Keep planning</h2>
        <ul className="mt-3 grid gap-2 sm:grid-cols-2">
          <li><Link to="/bosses" className="text-sm text-primary hover:underline">Full Palworld boss database</Link></li>
          <li><Link to="/map" className="text-sm text-primary hover:underline">Interactive Palpagos map</Link></li>
          <li><Link to="/team-builder" className="text-sm text-primary hover:underline">Team builder for boss rush comps</Link></li>
          <li><Link to="/elements" className="text-sm text-primary hover:underline">Elemental weakness chart</Link></li>
          <li><Link to="/mounts" className="text-sm text-primary hover:underline">Mount speeds for travel legs</Link></li>
          <li><Link to="/guides/$slug" params={{ slug: "palworld-all-bosses-guide" }} className="text-sm text-primary hover:underline">All bosses guide &amp; FAQ</Link></li>
        </ul>
      </nav>
    </div>
  );
}
